'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { authApi } from '@/features/auth/api';
import type { LoginCredentials, User } from '@/types/auth';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  setHydrated: (value: boolean) => void;
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (credentials: LoginCredentials) => Promise<User>;
  logout: () => Promise<void>;
  hydrateSession: () => Promise<void>;
  resetLogoutState: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isHydrated: false,
      isLoading: false,
      isLoggingOut: false,

      setHydrated: (value) => set({ isHydrated: value }),

      setUser: (user) =>
        set({
          user,
          isAuthenticated: Boolean(user),
        }),

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      resetLogoutState: () => set({ isLoggingOut: false }),

      login: async (credentials) => {
        set({ isLoading: true, isLoggingOut: false });
        try {
          const response = await authApi.login(credentials);

          const accessToken = response.data.accessToken;

          // Sync token to browser cookie so Next.js middleware proxy can read it
          if (typeof document !== 'undefined') {
            document.cookie = `dc_admin_access_token=${accessToken}; path=/; max-age=86400; SameSite=Lax`;
          }

          set({
            user: response.data.user,
            accessToken: accessToken,
            refreshToken: response.data.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return response.data.user;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true, isLoggingOut: true });
        try {
          // Send logout to backend with the current token (handled in apiClient interceptor or we pass token)
          // Actually apiClient will attach the token automatically via the request interceptor we will add
          if (get().refreshToken) {
            await authApi.logout(get().refreshToken!);
          }
        } catch {
          // Ignore failures on logout
        } finally {
          // Remove cookie
          if (typeof document !== 'undefined') {
            document.cookie = 'dc_admin_access_token=; path=/; max-age=0; SameSite=Lax';
          }

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      hydrateSession: async () => {
        set({ isHydrated: true });
      },
    }),
    {
      name: 'dc-auth-store',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    },
  ),
);
